import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function DeleteTenantDialog({ tenant, open, onOpenChange, onDeleted }) {
  const [erase, setErase] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.delete(`/tenants/${tenant.id}`, { params: { erase_history: erase } });
      toast.success(`${data.name} deleted${erase ? " along with their payment history" : " — payment history kept"}`);
      onOpenChange(false);
      onDeleted();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  if (!tenant) return null;
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) setErase(false); } }}>
      <AlertDialogContent data-testid="delete-tenant-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><span className="h-9 w-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center"><Trash2 className="h-4 w-4" /></span> Delete {tenant.name}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-600">
              <p>This permanently deletes the user account for <b className="text-slate-800">{tenant.email}</b>. They will no longer be able to sign in.</p>
              <ul className="list-disc pl-5 space-y-0.5 text-slate-500">
                <li>Their active lease ends{tenant.lease?.property?.name ? <> — <b className="text-slate-700">{tenant.lease.property.name}</b> becomes vacant</> : ""}</li>
                <li>Messages, documents, maintenance requests and pending invites are removed</li>
                {tenant.lease?.autopay?.active && <li className="text-amber-700">Their auto-pay subscription is cancelled</li>}
              </ul>
              <label className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/60 p-3.5 cursor-pointer" data-testid="delete-tenant-erase-row">
                <Checkbox checked={erase} onCheckedChange={(v) => setErase(!!v)} className="mt-0.5" data-testid="delete-tenant-erase" />
                <span>
                  <Label className="text-sm font-semibold text-slate-900 cursor-pointer">Also erase payment history</Label>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {erase ? "All of their payments, late fees and lease records are wiped — they disappear from ledgers and the year-end tax summary." : "Unchecked: payments and fees stay in your ledgers and year-end summary, labelled “Former tenant”."}
                  </span>
                </span>
              </label>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="delete-tenant-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} disabled={busy} className="bg-rose-600 hover:bg-rose-700 gap-2" data-testid="delete-tenant-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {erase ? "Delete tenant & history" : "Delete tenant"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
